import { Badge, BadgeLevel, BadgeShape, BadgeSize, Text, TextColor, TextVariant } from '@vizit/atoms';
import { AmazonIcon, WalmartCircleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import type { ComponentType } from 'react';
import { BenchmarkMosaic, BenchmarkMosaicSize } from '../benchmark-mosaic';
import type { ScoredAgainstBenchmark, ScoredAgainstBoxProps, ScoredAgainstRetailer } from './types';

const RETAILER_LOGOS: Record<ScoredAgainstRetailer, { Icon: ComponentType<{ size?: number }>; label: string }> = {
  amazon: { Icon: AmazonIcon, label: 'Amazon' },
  walmart: { Icon: WalmartCircleIcon, label: 'Walmart' },
};

const labelProps = { variant: TextVariant.XsSemibold, color: TextColor.Tertiary, elementType: 'p' } as const;

function BenchmarkIdentity({ benchmark }: { benchmark: ScoredAgainstBenchmark }) {
  if (benchmark.kind === 'custom') {
    return (
      <>
        <BenchmarkMosaic name={benchmark.name} urls={benchmark.previewImageUrls} size={BenchmarkMosaicSize.Thumbnail} />
        <Badge level={BadgeLevel.Dark} shape={BadgeShape.Box} size={BadgeSize.Sm} bordered={false}>
          Custom
        </Badge>
      </>
    );
  }

  const logo = benchmark.retailer ? RETAILER_LOGOS[benchmark.retailer] : null;
  if (!logo) return null;

  return (
    <span className="flex shrink-0 items-center" role="img" aria-label={logo.label}>
      <logo.Icon size={20} />
    </span>
  );
}

/**
 * The benchmark and product category an image's score was taken against, in a
 * subtle gray box. Vizit benchmarks lead with their retailer logo; custom ones
 * with their image mosaic and a "Custom" badge.
 */
function ScoredAgainstBox(props: ScoredAgainstBoxProps) {
  const { benchmark, productCategory, className } = props;

  return (
    <section
      aria-label="Scored against"
      className={cx('flex flex-row gap-12 rounded-xs bg-secondary px-6 py-2', className)}
    >
      <div className="flex min-w-0 flex-col gap-2">
        <Text {...labelProps} className="leading-[var(--heading-height,24px)]">
          Benchmark
        </Text>
        <div className="flex min-w-0 flex-row items-center gap-3">
          <BenchmarkIdentity benchmark={benchmark} />
          <Text
            variant={TextVariant.XsRegular}
            className="truncate leading-[var(--text-height,20px)]"
            title={benchmark.name}
          >
            {benchmark.name}
          </Text>
        </div>
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <Text {...labelProps} className="leading-[var(--heading-height,24px)]">
          Product category
        </Text>
        <Text
          variant={TextVariant.XsRegular}
          className="truncate leading-[var(--text-height,20px)]"
          title={productCategory.name}
        >
          {productCategory.name}
        </Text>
      </div>
    </section>
  );
}

export { ScoredAgainstBox };
